<template>
    <div 
        class="user-permission-row" 
        :class="{ 
            'row-odd': isOdd, 
            'inactive': !isActive,
            'blur-row': !isActive,
            'current-user-row': isCurrentUser
        }"
    >
        <div class="target-cell">
            <div class="target-info">
                <i class="material-icons">person</i>
                <span class="target-name">{{ user.name }}</span>
                <span v-if="isCurrentUser" class="current-user-badge" v-tippy="{ content: 'Kendi yetkinizi değiştiremezsiniz' }">
                    <i class="material-icons">account_circle</i>
                    Siz
                </span>
                <span v-else-if="hasUserOverride" class="override-badge" v-tippy="{ content: 'Bu kullanıcı için özel yetki tanımlanmış. Departman yetkileri göz ardı edilir.' }">
                    <i class="material-icons">verified_user</i>
                    Özel Yetki
                </span>
            </div>
            <SupsisButton 
                v-if="!isActive && !isCurrentUser"
                ghost 
                primary
                icon="add" 
                small 
                @click="$emit('activate', user._id)"
                v-tippy="{ content: 'Yetki Ver' }"
            />
            <SupsisButton 
                v-else-if="isActive && !isCurrentUser"
                ghost 
                danger 
                icon="remove_circle" 
                small 
                @click="$emit('deactivate', user._id)"
                v-tippy="{ content: 'Yetkiyi Kaldır' }"
            />
        </div>

        <div 
            v-for="group in scopeGroups" 
            :key="group.id" 
            class="scope-group-cell"
        >
            <div class="scope-toggles">
                <div 
                    v-for="scope in group.scopes" 
                    :key="scope"
                    class="scope-toggle-cell"
                    :class="{ 
                        'root-cell': isCurrentUser,
                        'from-user': hasScopeFromUser(scope),
                        'from-department': hasScopeFromDepartment(scope) && !hasUserOverride
                    }"
                    @click="!isCurrentUser && isActive && toggleScope(scope)"
                >
                    <SupsisButton 
                        :class="{ 'scope-active': hasScope(scope) }"
                        :disabled="!isActive || isCurrentUser"
                    >
                        <i class="material-icons" v-if="hasScope(scope)">done</i>
                        <div 
                            v-else 
                            class="disabled-item"
                            @mouseenter="isActive && hoverEffect($event, true)"
                            @mouseleave="isActive && hoverEffect($event, false)"
                        >
                            <i class="material-icons">done</i>
                            <i class="material-icons">circle</i>
                        </div>
                    </SupsisButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "UserPermissionRow",
    components: {
        SupsisButton,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        target: {
            type: Object,
            default: null,
        },
        targetIndex: {
            type: Number,
            default: -1,
        },
        scopeGroups: {
            type: Array,
            required: true,
        },
        permissionTargets: {
            type: Array,
            default: () => [],
        },
        departments: {
            type: Array,
            default: () => [],
        },
        currentUserId: {
            type: String,
            required: true,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        isOdd: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isCurrentUser() {
            // Şu an auth olan kullanıcı mı?
            return this.user._id.toString() === this.currentUserId.toString();
        },
        hasUserOverride() {
            // Kullanıcıya özel yetki tanımlanmış mı?
            return this.isActive && !!this.target;
        },
        departmentScopes() {
            // Kullanıcının departmanlarından gelen tüm scope'ları merge et
            const userSubjects = this.user.subjects || [];
            const allScopes = [];

            // Eğer subjects boşsa, TÜM departmanların yetkilerine sahiptir
            if (!userSubjects || userSubjects.length === 0) {
                this.permissionTargets.forEach((target) => {
                    if (target.target === "department" && target.scopes) {
                        allScopes.push(...target.scopes);
                    }
                });
            } else {
                // Sadece kullanıcının dahil olduğu departmanların yetkilerini al
                userSubjects.forEach((subj) => {
                    const subjId = typeof subj === "object" ? subj._id : subj;
                    const deptTarget = this.permissionTargets.find(
                        (t) => t.target === "department" && t.department && t.department.toString() === subjId.toString()
                    );
                    if (deptTarget && deptTarget.scopes) {
                        allScopes.push(...deptTarget.scopes);
                    }
                });
            }

            // Unique scope'ları döndür
            return [...new Set(allScopes)];
        },
    },
    methods: {
        hasScope(scope) {
            // Kullanıcıya özel yetki varsa SADECE onu kontrol et (departman ignore edilir)
            if (this.hasUserOverride) {
                return (this.target.scopes || []).includes(scope);
            }
            
            // Kullanıcıya özel yetki yoksa departmanlardan gelen yetkiye bak
            return this.departmentScopes.includes(scope);
        },
        hasScopeFromUser(scope) {
            // Kullanıcıya özel tanımlanmış scope mu?
            if (!this.hasUserOverride) return false;
            return (this.target.scopes || []).includes(scope);
        },
        hasScopeFromDepartment(scope) {
            // Departmanlardan gelen scope mu?
            if (this.hasUserOverride) return false; // Kullanıcı override varsa departman ignore
            return this.departmentScopes.includes(scope);
        },
        toggleScope(scope) {
            // Şu anki kullanıcı kendisi üzerinde değişiklik yapamaz
            if (this.isCurrentUser || !this.isActive) return;
            this.$emit("toggle-scope", { targetIndex: this.targetIndex, scope });
        },
        hoverEffect(e, hover) {
            if (this.isCurrentUser) return;
            e.target.classList[hover ? "add" : "remove"]("active");
        },
    },
});
</script>

<style scoped lang="scss">
.user-permission-row {
    display: flex;
    border-bottom: 1px solid var(--color-border-default);
    min-height: 50px;
    transition: all 0.3s ease;

    &.row-odd {
        background: var(--color-bg-neutral-default);
    }

    &.inactive {
        opacity: 0.5;
        
        &.blur-row {
            filter: blur(0.5px);
        }
    }

    &.current-user-row {
        background: #fff9e6;
        border-left: 3px solid #faad14;
    }

    &:hover {
        background: var(--color-bg-neutral-hover);
        
        &.inactive {
            opacity: 0.8;
            filter: blur(0);
        }

        &.current-user-row {
            background: #fff7e6;
        }
    }

    .target-cell {
        width: 250px;
        min-width: 250px;
        flex-shrink: 0;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-right: 1px solid var(--color-border-default);

        .target-info {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;

            .material-icons {
                font-size: 18px;
                color: var(--color-icon-default);
                flex-shrink: 0;
            }

            .target-name {
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .override-badge {
                display: flex;
                align-items: center;
                gap: 3px;
                font-size: 11px;
                color: #1890ff;
                background: #e6f7ff;
                padding: 2px 8px;
                border-radius: 10px;
                white-space: nowrap;
                border: 1px solid #91d5ff;
                cursor: help;

                .material-icons {
                    font-size: 14px;
                }
            }

            .current-user-badge {
                display: flex;
                align-items: center;
                gap: 3px;
                font-size: 11px;
                color: #fa8c16;
                background: #fff7e6;
                padding: 2px 8px;
                border-radius: 10px;
                white-space: nowrap;
                border: 1px solid #ffd591;
                cursor: help;

                .material-icons {
                    font-size: 14px;
                }
            }
        }
    }

    .scope-group-cell {
        flex: 1;
        display: flex;
        border-right: 1px solid var(--color-border-default);

        &:last-child {
            border-right: none;
        }

        .scope-toggles {
            display: flex;
            width: 100%;

            .scope-toggle-cell {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                border-right: 1px solid var(--color-border-subdued);
                cursor: pointer;
                padding: 0;

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background: var(--color-bg-primary-subdued-hover);
                }

                &.root-cell {
                    cursor: not-allowed;
                    background: #fff9e6;

                    &:hover {
                        background: #fff7e6;
                    }
                }

                &.from-user {
                    background: rgba(82, 196, 26, 0.08);
                }

                &.from-department {
                    background: rgba(24, 144, 255, 0.05);
                }

                &.user-override-active {
                    // Kullanıcı override varsa departman rengi gösterilmesin
                    &.from-department {
                        background: transparent;
                    }
                }

                .supsis-button {
                    width: 100%;
                    height: 100%;
                    min-height: 50px;
                    border: none;
                    background: transparent;
                    padding: 0;

                    &.scope-active {
                        .material-icons {
                            color: var(--color-icon-success);
                        }
                    }

                    .disabled-item {
                        :first-child {
                            display: none;
                        }

                        &.active {
                            :first-child {
                                display: block;
                            }
                            :last-child {
                                display: none;
                            }
                        }
                    }

                    .material-icons {
                        font-size: 20px;
                    }
                }
            }
        }
    }
}
</style>

